import { useEffect, useMemo } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { IBuiltInInstrumentSummary } from '@brushfire/types'
import { useBuiltInInstrumentDetail } from '@/features/built-in-instruments/api/built-in-instruments-queries'
import { builtInQuestionsToPreviewRows } from '@/features/built-in-instruments/lib/builtInInstrumentMappers'
import { formatTemplateEstCompletionLabel } from '@/features/instruments/templates/lib/formatTemplateEstCompletion'
import { libraryModalCloseButtonSx } from '@/features/library/lib/libraryModalCloseButtonSx'
import { TemplatePreviewQuestionsSkeleton } from '@/features/instruments/templates/components/skeletons'
import { TemplatePreviewQuestionCard } from '@/features/instruments/templates/components/TemplatePreviewQuestionCard'

const HEADER_BORDER = '#f0f4f8'
const TITLE = '#0d1b2a'
const FOOTER_BORDER = '#e4e9f0'

type Props = Readonly<{
  open: boolean
  entry: IBuiltInInstrumentSummary | null
  onClose: () => void
  /** Create Instrument → Browse from Template — show Use this template when set. */
  onUseTemplate?: (entry: IBuiltInInstrumentSummary) => void
}>

export function BuiltInInstrumentPreviewModal({
  open,
  entry,
  onClose,
  onUseTemplate,
}: Props) {
  const entryId = entry?.id ?? null
  const { data: detail, isLoading, isError } = useBuiltInInstrumentDetail(entryId, open && Boolean(entryId))

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const previewRows = useMemo(
    () => (detail ? builtInQuestionsToPreviewRows(detail) : []),
    [detail],
  )

  const addedByLabel = detail?.savedByName ?? entry?.savedByName ?? ''
  const showUseCta = typeof onUseTemplate === 'function'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      data-testid="built-in-instrument-preview-modal"
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.5)' } },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          width: '100%',
          maxWidth: 640,
          bgcolor: '#ffffff',
          borderRadius: '16px',
          border: '0.5px solid #e4e9f0',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          m: 2,
          overflow: 'hidden',
        },
      }}
      aria-labelledby="built-in-instrument-preview-title"
    >
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: '#ffffff',
          borderBottom: `1px solid ${HEADER_BORDER}`,
          px: 3.5,
          pt: 3,
          pb: 2,
        }}
      >
        <Typography
          id="built-in-instrument-preview-title"
          sx={{
            fontWeight: 700,
            fontSize: '20px',
            lineHeight: '30px',
            color: TITLE,
            mb: 2,
          }}
        >
          {entry?.name ?? 'Instrument preview'}
        </Typography>

        {entry ? (
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
            <Box
              sx={{
                px: 1.5,
                py: 0.875,
                borderRadius: 999,
                bgcolor: '#f4f7fa',
                border: '1px solid #e4e9f0',
              }}
            >
              <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: '#64748b' }}>
                {entry.questionCount} questions
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1.5,
                py: 0.875,
                borderRadius: 999,
                bgcolor: '#e6f4fb',
                border: '1px solid #c7ebf9',
              }}
            >
              <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: '#008cd0' }}>
                {formatTemplateEstCompletionLabel(
                  detail?.estCompletionSecs ?? entry.estCompletionSecs,
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1.5,
                py: 0.875,
                borderRadius: 999,
                bgcolor: '#f4f7fa',
                border: '1px solid #e4e9f0',
              }}
            >
              <Typography
                data-testid="built-in-instrument-preview-usage-count"
                sx={{ fontSize: '14px', lineHeight: '20px', color: '#92400e' }}
              >
                Used {entry.usageCount} times
              </Typography>
            </Box>
          </Stack>
        ) : null}

        {addedByLabel ? (
          <Typography
            data-testid="built-in-instrument-preview-added-by"
            sx={{ fontSize: '14px', lineHeight: '20px', color: '#64748b' }}
          >
            Added by: {addedByLabel}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3.5,
          py: 2,
          bgcolor: '#fafbfc',
        }}
      >
        {isLoading ? <TemplatePreviewQuestionsSkeleton /> : null}
        {isError ? (
          <Typography sx={{ color: '#b91c1c', textAlign: 'center', py: 3 }}>
            Could not load instrument preview.
          </Typography>
        ) : null}
        {!isLoading && !isError
          ? previewRows.map((row, index) => (
              <TemplatePreviewQuestionCard
                key={`${row.apiType}-${index}`}
                index={index + 1}
                apiType={row.apiType}
                prompt={row.prompt}
                builderQuestion={row.builderQuestion}
              />
            ))
          : null}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          borderTop: `1px solid ${FOOTER_BORDER}`,
          px: 3.5,
          py: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          bgcolor: '#ffffff',
        }}
      >
        <Button
          variant={showUseCta ? 'outlined' : 'contained'}
          color={showUseCta ? 'secondary' : 'primary'}
          onClick={onClose}
          data-testid="built-in-instrument-preview-close"
          sx={
            showUseCta
              ? {
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 500,
                  borderColor: '#cbd5e1',
                  color: '#475569',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    bgcolor: '#f8fafc',
                  },
                }
              : libraryModalCloseButtonSx
          }
        >
          Close
        </Button>
        {showUseCta ? (
          <Button
            variant="contained"
            disabled={!entry}
            onClick={() => {
              if (entry && onUseTemplate) onUseTemplate(entry)
            }}
            data-testid="built-in-instrument-preview-use"
            sx={libraryModalCloseButtonSx}
          >
            Use this template
          </Button>
        ) : null}
      </Box>
    </Dialog>
  )
}
